'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@nextui-org/react';
import Link from 'next/link';
import { useParams } from 'next/navigation';

export default function NotFound() {
  const [countdown, setCountdown] = useState(15);
  const [isRedirecting, setIsRedirecting] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // eslint-disable-next-line no-unused-vars
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const params = useParams();

  const lang = useMemo(() => {
    const maybeLang = (params as Record<string, string | string[]>)?.lang;

    if (typeof maybeLang === 'string' && maybeLang.length > 0) {
      return maybeLang;
    }

    if (Array.isArray(maybeLang) && maybeLang.length > 0) {
      return maybeLang[0];
    }

    return undefined;
  }, [params]);

  const homePath = useMemo(() => (lang ? `/${lang}` : '/'), [lang]);

  const withLocale = useCallback((path: string) => {
    if (!path) {
      return homePath;
    }

    if (path.startsWith('http')) {
      return path;
    }

    if (!lang) {
      return path;
    }

    if (path === '/' || path === '') {
      return homePath;
    }

    return `/${lang}${path.startsWith('/') ? path : `/${path}`}`;
  }, [homePath, lang]);

  // Countdown timer for automatic redirect
  useEffect(() => {
    if (isRedirecting && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isRedirecting && countdown === 0) {
      // Redirect to home page
      window.location.href = homePath;
    }
  }, [countdown, homePath, isRedirecting]);

  const cancelRedirect = () => {
    setIsRedirecting(false);
  };

  const restartRedirect = () => {
    setCountdown(15);
    setIsRedirecting(true);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // You can implement search functionality here
  window.location.href = withLocale(`/search?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900">
      {/* HTML Meta Refresh Fallback */}
      <noscript>
        <meta httpEquiv="refresh" content={`10; url=${homePath}`} />
      </noscript>

      <div className="container mx-auto px-4 py-8 sm:py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header with animated background */}
          <div className="text-center mb-12">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full blur-3xl opacity-30 animate-pulse"></div>
              <div className="relative text-8xl sm:text-9xl md:text-[12rem] font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-400 dark:via-purple-400 dark:to-indigo-400 select-none">
                404
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                Oops! Page Not Found
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                The page you're looking for seems to have wandered off into the digital wilderness.
                Don't worry, we'll help you find your way back!
              </p>
            </div>
          </div>

          {/* Auto-redirect section */}
          {isRedirecting && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 mb-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
                    <svg className="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Auto-redirecting you home
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Taking you back to safety in <span className="font-bold text-blue-600 dark:text-blue-400">{countdown}</span> seconds
                  </p>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${(countdown / 15) * 100}%` }}
                  ></div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={cancelRedirect}
                    variant="bordered"
                    size="sm"
                    className="border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Stay Here
                  </Button>
                  <Button
                    onClick={restartRedirect}
                    size="sm"
                    className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                  >
                    Restart Timer
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Search section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 mb-8 max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                🔍 Search Our Site
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Maybe what you're looking for is just a search away
              </p>
            </div>

            <form onSubmit={handleSearch} className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  placeholder="Search for articles, guides, or resources..."
                  className="w-full px-4 py-3 pl-12 pr-12 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                <div className="absolute left-3 top-3.5">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-semibold py-3 rounded-xl transition-all duration-200"
                disabled={!searchQuery.trim()}
              >
                Search Now
              </Button>
            </form>
          </div>

          {/* Quick navigation */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Link
              href={homePath}
              className="group bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Home</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Return to our homepage</p>
              </div>
            </Link>

            <Link
              href={withLocale('/about')}
              className="group bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">About Us</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn more about our mission</p>
              </div>
            </Link>

            <Link
              href={withLocale('/contact')}
              className="group bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Contact</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Get in touch with our team</p>
              </div>
            </Link>
          </div>

          {/* Popular pages */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-6">
              🌟 Popular Pages
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { name: 'Programs', href: '/programs', icon: '📚' },
                { name: 'Articles', href: '/articles', icon: '📰' },
                { name: 'Reports', href: '/reports', icon: '📊' },
                { name: 'FAQ', href: '/faq', icon: '❓' },
                { name: 'Support', href: '/support', icon: '🛠️' },
                { name: 'Blog', href: '/blog', icon: '📝' },
                { name: 'Resources', href: '/resources', icon: '📁' },
                { name: 'Help', href: '/help', icon: '💡' }
              ].map((item) => (
                <Link
                  key={item.name}
                  href={withLocale(item.href)}
                  className="flex flex-col items-center p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 group"
                >
                  <span className="text-2xl mb-2 group-hover:scale-110 transition-transform duration-200">
                    {item.icon}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {item.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Help section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 sm:p-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Still can't find what you're looking for?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Our support team is here to help you navigate to the right place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  as={Link}
                  href={withLocale('/contact')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200"
                >
                  Contact Support
                </Button>
                <Button
                  as={Link}
                  href={withLocale('/sitemap')}
                  variant="bordered"
                  className="border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 px-6 py-3 rounded-xl transition-all duration-200"
                >
                  View Sitemap
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
